/**
 * Manages the complete flow of saving an address, updating the address list, and selecting the new address
 */
export default class AddressSaveHandler {
  /**
   * Creates a new address save handler with all the needed functions and references
   *
   * @param {Object} params - Configuration object containing all dependencies
   * @param {Function} params.saveAddressFn - Function that saves the address to the server
   * @param {Function} params.selectNewlyCreatedAddressFn - Function that selects the newly created address from the list
   * @param {Function} params.resetFormFn - Function that clears the form fields
   * @param {Function} params.emitFn - Function that sends events to parent components
   * @param {Object} params.isSubmittingRef - Reactive reference that tracks if the form is being submitted
   * @param {Object} params.store - Vuex store instance for managing app state
   */
  constructor({
    saveAddressFn,
    selectNewlyCreatedAddressFn,
    resetFormFn,
    emitFn,
    isSubmittingRef,
    store
  }) {
    this.saveAddressFn = saveAddressFn;
    this.selectNewlyCreatedAddressFn = selectNewlyCreatedAddressFn;
    this.resetFormFn = resetFormFn;
    this.emitFn = emitFn;
    this.isSubmittingRef = isSubmittingRef;
    this.store = store;
  }

  /**
   * Runs the complete address save process: saves the address, refreshes the address list,
   * selects the new address, and closes the form
   *
   * @param {string|null} authToken - Optional authentication token for two-factor authentication
   * @returns {Promise<void>}
   */
  async execute(authToken = null) {
    const result = await this.saveAddressFn(authToken);
    if (!result.success) {
      this.isSubmittingRef.value = false;
      return;
    }

    this.isSubmittingRef.value = true;
    const addressResponse = await this.store.dispatch("addresses/fetchAddresses");

    try {
      await this.selectNewlyCreatedAddressFn(addressResponse);
      this.isSubmittingRef.value = false;
      this.resetFormFn();
      this.emitFn("close");
    } catch (error) {
      this.isSubmittingRef.value = false;
      // eslint-disable-next-line no-console
      console.error(error);
      window.Honeybadger?.notify("Error selecting newly created address", {
        params: {
          error: JSON.stringify(error, Object.getOwnPropertyNames(error))
        }
      });
    }
  }
}
